import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Upload, Palette, Trash2, Image, MapPin, Store } from 'lucide-react';
import api from '@/lib/api';
import { formatDateFromIso } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import { useGym } from '@/contexts/GymContext';
import MapPicker from '@/components/ui/MapPicker';

export default function OwnerGymProfile() {
    const { selectedGym } = useGym();
    const [gym, setGym] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: '', address: '', phone: '', email: '', lat: '', lng: '', card_bg_color: '', card_text_color: '', store_enabled: false });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadingBanner, setUploadingBanner] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const fetch = async () => {
            try {
                const gymId = selectedGym?.id;
                const [gymRes, subRes] = await Promise.all([
                    api.get('/owner/gym', { params: gymId ? { gym_id: gymId } : {} }),
                    api.get('/owner/subscription'),
                ]);
                setGym(gymRes.data.data);
                setSubscription(subRes.data.data);
                setForm({
                    name: gymRes.data.data.name || '',
                    address: gymRes.data.data.address || '',
                    phone: gymRes.data.data.phone || '',
                    email: gymRes.data.data.email || '',
                    lat: gymRes.data.data.lat ?? '',
                    lng: gymRes.data.data.lng ?? '',
                    card_bg_color: gymRes.data.data.card_bg_color || '',
                    card_text_color: gymRes.data.data.card_text_color || '',
                    store_enabled: gymRes.data.data.store_enabled ?? false,
                });
            } catch {} finally { setLoading(false); }
        };
        fetch();
    }, [selectedGym?.id]);

    const handleSave = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        setSaving(true);
        setMessage('');
        try {
            const payload = {
                ...form,
                lat: form.lat ? parseFloat(form.lat) : undefined,
                lng: form.lng ? parseFloat(form.lng) : undefined,
                gym_id: selectedGym?.id || undefined,
            };
            const res = await api.put('/owner/gym', payload);
            setGym(res.data.data);
            setMessage('Gimnasio actualizado exitosamente.');
        } catch {} finally { setSaving(false); }
    };

    const handleBannerUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingBanner(true);
        const formData = new FormData();
        formData.append('banner', file);
        if (selectedGym?.id) formData.append('gym_id', selectedGym.id);
        try {
            const res = await api.post('/owner/gym/banner', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setGym(res.data.data);
            setMessage('Banner actualizado exitosamente.');
        } catch {} finally { setUploadingBanner(false); }
    };

    const handleRemoveBanner = async () => {
        try {
            const res = await api.delete('/owner/gym/banner', {
                data: selectedGym?.id ? { gym_id: selectedGym.id } : {},
            });
            setGym(res.data.data);
            setMessage('Banner eliminado.');
        } catch {}
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('logo', file);
        if (selectedGym?.id) formData.append('gym_id', selectedGym.id);
        try {
            const res = await api.post('/owner/gym/logo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setGym(res.data.data);
            setMessage('Logo actualizado exitosamente.');
        } catch {} finally { setUploading(false); }
    };

    if (loading) return <p className="text-white/60 text-center py-8">Cargando...</p>;

    return (
        <div>
            <h1 className="text-2xl font-bold uppercase tracking-wide text-white mb-6">Mi Gimnasio</h1>

            <div className="grid gap-6 lg:grid-cols-2">
                <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                    <CardHeader>
                        <CardTitle className="text-white uppercase tracking-wide">Información del Gimnasio</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSave} className="space-y-4">
                            {message && <div className="bg-white/5 text-white text-sm p-3 rounded-md">{message}</div>}
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Nombre</Label>
                                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Dirección</Label>
                                <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Teléfono</Label>
                                    <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Email</Label>
                                    <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />Ubicación en el mapa
                                </Label>
                                <MapPicker
                                    defaultPosition={form.lat ? { lat: parseFloat(form.lat), lng: parseFloat(form.lng) } : null}
                                    onChange={({ lat, lng }) => setForm({ ...form, lat: String(lat), lng: String(lng) })}
                                />
                            </div>
                            <div className="flex items-center justify-between p-3 rounded border border-[#3a3a3f] bg-white/5">
                                <div className="flex items-center gap-2">
                                    <Store className="h-4 w-4 text-white/60" />
                                    <div>
                                        <p className="text-sm text-white font-medium">Tienda</p>
                                        <p className="text-xs text-white/50">Permitir a los alumnos ver y comprar productos.</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, store_enabled: !form.store_enabled })}
                                    className={cn(
                                        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                                        form.store_enabled ? "bg-white" : "bg-white/20"
                                    )}
                                >
                                    <span
                                        className={cn(
                                            "inline-block h-4 w-4 transform rounded-full bg-black transition-transform",
                                            form.store_enabled ? "translate-x-6" : "translate-x-1"
                                        )}
                                    />
                                </button>
                            </div>
                            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar Cambios'}</Button>
                        </form>
                    </CardContent>
                </Card>

                <div className="space-y-6">
                    <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                        <CardHeader>
                            <CardTitle className="text-white uppercase tracking-wide">Logo</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-4">
                                {gym?.logo ? (
                                    <img src={`/storage/${gym.logo}`} alt="Logo" className="h-20 w-20 object-contain rounded-lg border border-[#3a3a3f]" />
                                ) : (
                                    <div className="h-20 w-20 bg-[#0a0a0a] rounded-lg flex items-center justify-center border border-[#3a3a3f]">
                                        <Building2 className="h-8 w-8 text-white/60" />
                                    </div>
                                )}
                                <div>
                                    <label className="cursor-pointer">
                                        <Button variant="outline" size="sm" asChild className="border-[#3a3a3f] text-white hover:bg-white/5">
                                            <span><Upload className="h-4 w-4 mr-2" />{uploading ? 'Subiendo...' : 'Cambiar logo'}</span>
                                        </Button>
                                        <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} disabled={uploading} />
                                    </label>
                                    <p className="text-xs text-white/60 mt-1">JPG, PNG, SVG. Máx 2MB.</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-white uppercase tracking-wide"><Palette className="h-5 w-5" />Personalización de Tarjeta</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-xs text-white/60">Estos colores se usan en la tarjeta pública que ven tus alumnos al consultar su DNI.</p>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Color de fondo</Label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={form.card_bg_color || '#6d28d9'}
                                            onChange={e => setForm({ ...form, card_bg_color: e.target.value })}
                                            className="h-9 w-12 rounded border border-[#3a3a3f] cursor-pointer bg-[#0a0a0a]"
                                        />
                                        <Input
                                            value={form.card_bg_color}
                                            onChange={e => setForm({ ...form, card_bg_color: e.target.value })}
                                            placeholder="#6d28d9"
                                            className="flex-1 font-mono text-xs bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Color de texto</Label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={form.card_text_color || '#ffffff'}
                                            onChange={e => setForm({ ...form, card_text_color: e.target.value })}
                                            className="h-9 w-12 rounded border border-[#3a3a3f] cursor-pointer bg-[#0a0a0a]"
                                        />
                                        <Input
                                            value={form.card_text_color}
                                            onChange={e => setForm({ ...form, card_text_color: e.target.value })}
                                            placeholder="#ffffff"
                                            className="flex-1 font-mono text-xs bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white"
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Preview */}
                            <div>
                                <Label className="mb-2 block text-white/60 uppercase tracking-wider text-xs">Vista previa</Label>
                                <div
                                    className="rounded-lg p-4 relative overflow-hidden"
                                    style={{
                                        backgroundColor: form.card_bg_color || '#6d28d9',
                                        color: form.card_text_color || '#ffffff',
                                        backgroundImage: gym?.card_banner ? `url(/storage/${gym.card_banner})` : 'none',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                    }}
                                >
                                    {gym?.card_banner && <div className="absolute inset-0 bg-black/40" />}
                                    <div className="relative z-10">
                                        <p className="font-semibold text-sm">{gym?.name || 'Mi Gimnasio'}</p>
                                        <p className="text-lg font-bold mt-1">Juan Pérez</p>
                                    </div>
                                </div>
                            </div>
                            <Button onClick={handleSave} disabled={saving} size="sm">{saving ? 'Guardando...' : 'Guardar Colores'}</Button>

                            {/* Banner */}
                            <div className="border-t border-[#3a3a3f] pt-4 space-y-3">
                                <Label className="flex items-center gap-2 text-white/60 uppercase tracking-wider text-xs"><Image className="h-4 w-4" />Banner de fondo (opcional)</Label>
                                <p className="text-xs text-white/60">Se muestra como imagen de fondo en el encabezado de la tarjeta. Recomendado: 600x200px.</p>
                                <div className="flex items-center gap-3">
                                    {gym?.card_banner ? (
                                        <img src={`/storage/${gym.card_banner}`} alt="Banner" className="h-16 w-32 object-cover rounded border border-[#3a3a3f]" />
                                    ) : (
                                        <div className="h-16 w-32 bg-[#0a0a0a] rounded flex items-center justify-center border border-[#3a3a3f]">
                                            <Image className="h-6 w-6 text-white/60" />
                                        </div>
                                    )}
                                    <div className="space-y-1">
                                        <label className="cursor-pointer">
                                            <Button variant="outline" size="sm" asChild className="border-[#3a3a3f] text-white hover:bg-white/5">
                                                <span><Upload className="h-4 w-4 mr-2" />{uploadingBanner ? 'Subiendo...' : 'Subir banner'}</span>
                                            </Button>
                                            <input type="file" className="hidden" accept="image/*" onChange={handleBannerUpload} disabled={uploadingBanner} />
                                        </label>
                                        {gym?.card_banner && (
                                            <Button variant="ghost" size="sm" className="text-white hover:bg-white/5" onClick={handleRemoveBanner}>
                                                <Trash2 className="h-3 w-3 mr-1" />Quitar
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                        <CardHeader>
                            <CardTitle className="text-white uppercase tracking-wide">Suscripción</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {subscription ? (
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-white/60">Plan</span>
                                        <span className="font-semibold text-white">{subscription.plan?.name || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-white/60">Estado</span>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${subscription.status === 'active' ? 'bg-white text-black' : 'bg-white/10 text-white'}`}>
                                            {subscription.status === 'active' ? 'Activo' : subscription.status}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-white/60">Máx. gimnasios</span>
                                        <span className="text-white">{subscription.plan?.max_gyms || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-white/60">Máx. alumnos/gym</span>
                                        <span className="text-white">{subscription.plan?.max_customers_per_gym || '-'}</span>
                                    </div>
                                    {subscription.is_lifetime && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/10 text-white">De por vida</span>
                                    )}
                                    {!subscription.is_lifetime && subscription.ends_at && (
                                        <div className="flex justify-between">
                                            <span className="text-white/60">Vence</span>
                                            <span className="text-white">{formatDateFromIso(subscription.ends_at)}</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-white/60">Sin suscripción activa.</p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
